import { useCallback, useState, type FormEvent } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import SeoHead from '../components/SeoHead';
import { brandingFixture } from '../lib/branding/branding-fixture';
import { getCognitoConfig } from '../lib/auth/cognito-config';
import {
  confirmSignUp,
  resendConfirmationCode,
  signInWithEmailPassword,
} from '../lib/auth/email-password';
import { storeTokens } from '../lib/auth/token-storage';
import { useAppDispatch } from '../store/hooks';
import { authSignedIn, authSignInFailed } from '../store/slices/auth-slice';
import { upsertProfileThunk } from '../store/thunks/profile-thunks';
import {
  clearPendingProfile,
  loadPendingProfile,
} from './SignUpPage';

/**
 * Verify-email surface (round-22 Phase D).
 *
 * Second step of the SPA sign-up flow. Reads the pending
 * profile from sessionStorage (stashed by `<SignUpPage>`),
 * asks the user for the six-digit verification code Cognito
 * emailed, then:
 *
 *   1. `confirmSignUp(email, code)`
 *   2. `signInWithEmailPassword(email, password)` — password
 *      lives in the pending payload so the user does not have
 *      to re-type it.
 *   3. Persist tokens + dispatch `authSignedIn`.
 *   4. `upsertProfileThunk({ username, firstName, lastName,
 *      phoneE164, preferredPlatforms, optInNewsletter })`.
 *   5. Clear pending profile + navigate to `/`.
 *
 * A "resend code" affordance calls `resendConfirmationCode`.
 *
 * If no pending profile is present (user landed here directly),
 * the page collects email + code manually and skips the
 * password-based sign-in step — the user finishes via
 * `<SignInPage>` after confirming.
 */

export default function VerifyEmailPage(): JSX.Element {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { userPoolId, userPoolClientId } = getCognitoConfig();
  const pending = loadPendingProfile();
  const [email, setEmail] = useState(pending?.email ?? '');
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | undefined>(undefined);
  const [info, setInfo] = useState<string | undefined>(undefined);
  const [submitting, setSubmitting] = useState(false);

  const finishProfile = useCallback(async () => {
    if (pending === null) return;
    const signIn = await signInWithEmailPassword({
      userPoolId,
      userPoolClientId,
      email: pending.email,
      password: pending.password,
    });
    storeTokens(userPoolClientId, {
      idToken: signIn.idToken,
      accessToken: signIn.accessToken,
      refreshToken: signIn.refreshToken,
      expiresAt: Date.now() + signIn.expiresIn * 1000,
    });
    dispatch(
      authSignedIn({
        idToken: signIn.idToken,
        accessToken: signIn.accessToken,
        cognitoSub: signIn.cognitoSub,
      }),
    );
    await dispatch(
      upsertProfileThunk({
        username: pending.username,
        firstName: pending.firstName,
        lastName: pending.lastName,
        phoneE164: pending.phoneE164,
        dateOfBirth: pending.dateOfBirth,
        preferredPlatforms: pending.preferredPlatforms,
        optInNewsletter: pending.optInNewsletter,
      }),
    );
    clearPendingProfile();
    navigate('/');
  }, [dispatch, navigate, pending, userPoolClientId, userPoolId]);

  const handleSubmit = useCallback(
    async (evt: FormEvent<HTMLFormElement>) => {
      evt.preventDefault();
      setError(undefined);
      setInfo(undefined);
      setSubmitting(true);
      try {
        await confirmSignUp({
          userPoolId,
          userPoolClientId,
          email,
          code,
        });
        if (pending !== null && pending.email === email) {
          await finishProfile();
        } else {
          setInfo('Email verified. You can now sign in.');
          navigate('/sign-in');
        }
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'verification failed';
        setError(msg);
        dispatch(authSignInFailed({ error: msg }));
      } finally {
        setSubmitting(false);
      }
    },
    [
      code,
      dispatch,
      email,
      finishProfile,
      navigate,
      pending,
      userPoolClientId,
      userPoolId,
    ],
  );

  const handleResend = useCallback(async () => {
    setError(undefined);
    setInfo(undefined);
    try {
      await resendConfirmationCode({ userPoolId, userPoolClientId, email });
      setInfo('New code sent. Check your email.');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'resend failed');
    }
  }, [email, userPoolClientId, userPoolId]);

  return (
    <Card sx={{ maxWidth: 480, mx: 'auto', mt: 4 }}>
      <CardContent>
        <SeoHead
          route="/verify-email"
          pageTitle="Verify email"
          branding={brandingFixture.share}
          jsonLdType="WebSite"
        />
        <Typography variant="h1" component="h1" sx={{ fontSize: '1.75rem', mb: 2 }}>
          Verify your email
        </Typography>
        <Typography variant="body2" sx={{ mb: 2 }}>
          We sent a six-digit code to your email. Enter it below to finish
          creating your account.
        </Typography>
        <Box component="form" onSubmit={handleSubmit} noValidate>
          <Stack spacing={2}>
            <TextField
              label="Email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              disabled={pending !== null}
              fullWidth
              autoComplete="email"
            />
            <TextField
              label="Verification code"
              value={code}
              onChange={(e) => setCode(e.target.value.trim())}
              required
              fullWidth
              slotProps={{
                htmlInput: {
                  inputMode: 'numeric',
                  pattern: '[0-9]*',
                  maxLength: 8,
                },
              }}
              autoComplete="one-time-code"
            />
            {error && <Alert severity="error">{error}</Alert>}
            {info && <Alert severity="info">{info}</Alert>}
            <Button type="submit" variant="contained" disabled={submitting}>
              {submitting ? 'Verifying…' : 'Verify'}
            </Button>
            <Button variant="text" onClick={handleResend}>
              Resend code
            </Button>
          </Stack>
        </Box>
      </CardContent>
    </Card>
  );
}
